import React, { useState, useEffect, useContext } from "react";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import MButton from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import ModalDialogContext from "../../Context/ModalDialog/ModalDialogContext";
import { LoadingIndicator, NoAccessScreen } from "../../Component";
import {
  DocumentRequestTable,
  CreateDocumentRequest,
  ViewRequest,
} from "./Component";
import { recordTypes } from "../../API/config";
import { getDocumentRequests } from "../../API/Entity/DocumentRequest";
import styles from "./styles";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

const DocumentRequests = (props) => {
  const { entity, hasRecordPermission, reloadEntity } = props;
  const classes = useStyles();

  const [documentRequests, setDocumentRequests] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [canRequestDocuments, setCanRequestDocuments] = useState();
  const [canViewDocumentRequests, setCanViewDocumentRequests] = useState(true);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState();

  const { t } = useTranslation();

  const { showErrorMessage } = useContext(ModalDialogContext);

  useEffect(() => {
    const setPermissions = async () => {
      const { permissions = {} } = entity;
      const canRequestDocuments = hasRecordPermission(
        permissions,
        recordTypes.record.actions.requestClientDocuments.name,
      );
      setCanRequestDocuments(canRequestDocuments);
      const canViewDocumentRequests = hasRecordPermission(
        permissions,
        recordTypes.record.actions.viewClientDocumentRequests.name,
      );
      setCanViewDocumentRequests(canViewDocumentRequests);
    };
    setPermissions();
    setReload(true);
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const loadDocumentReqs = async () => {
      try {
        setLoading(true);
        const { id } = entity;
        const result = await getDocumentRequests(id);
        const { data = [] } = result || {};
        if (!isCancelled) {
          setDocumentRequests(data);
        }
      } catch (error) {
        showErrorMessage({ error });
      }
      if (!isCancelled) {
        setLoading(false);
        setReload(false);
      }
    };
    if (reload) {
      loadDocumentReqs();
    }
    return () => {
      isCancelled = true;
    };
  }, [reload]);

  const openNewRequestDialog = () => {
    setIsAddDialogOpen(true);
  };

  const closeNewRequestDialog = (status) => {
    setIsAddDialogOpen(false);
    if (status !== "cancel") {
      setReload(true);
    }
  };

  const openViewRequestDialog = () => {
    setIsViewDialogOpen(true);
  };

  const closeViewRequestDialog = (status) => {
    setIsViewDialogOpen(false);
  };

  const onRowViewClick = async (documentRequest) => {
    setCurrentRequest(documentRequest);
    openViewRequestDialog(true);
  };

  if (!canViewDocumentRequests) {
    return <NoAccessScreen />;
  }

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  return (
    <div className={classes.pageContent}>
      <div className={classes.headerBarContainer}>
        <Grid container direction="row" spacing={1}>
          <Grid
            item
            xs={6}
            container
            justifyContent="flex-start"
            alignItems="center"
          >
            <Typography component="span" className={classes.headerBarTitle}>
              {t("labels.record.documentRequest.header")}
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            {canRequestDocuments && (
              <MButton
                color="primary"
                variant="contained"
                label={t("labels.common.add")}
                style={{ height: 30 }}
                onClick={openNewRequestDialog}
              >
                <AddIcon />
              </MButton>
            )}
          </Grid>
        </Grid>
      </div>
      <div className={classes.tableContainer}>
        {isLoading ? (
          renderLoadingIndicator()
        ) : (
          <DocumentRequestTable
            documentRequests={documentRequests}
            onRowViewClick={onRowViewClick}
          />
        )}
      </div>
      {canRequestDocuments && isAddDialogOpen && (
        <CreateDocumentRequest
          open={isAddDialogOpen}
          onClose={closeNewRequestDialog}
          entity={entity}
          reloadEntity={reloadEntity}
          documentRequests={documentRequests}
        />
      )}
      {isViewDialogOpen && (
        <ViewRequest
          open={isViewDialogOpen}
          onClose={closeViewRequestDialog}
          documentRequest={currentRequest}
        />
      )}
    </div>
  );
};

export default DocumentRequests;
